import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiMail, FiCheckCircle } from 'react-icons/fi';
import { Screen } from '../../components/ui/Screen';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { resendVerificationEmailWithCredentials } from '../../services/firebase/auth.service';
import { useAppContext } from '../../store/AppContext';

export function CheckEmailScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: userLoading } = useAppContext();
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  // Get email from location state (passed from RegisterScreen)
  useEffect(() => {
    const stateEmail = (location.state as { email?: string })?.email;
    if (stateEmail) {
      setEmail(stateEmail);
    } else {
      // If no email in state, redirect back to register
      navigate('/auth/register', { replace: true });
    }
  }, [location.state, navigate]);

  // Redirect if already logged in and verified
  useEffect(() => {
    if (!userLoading && user && user.emailVerified) {
      navigate('/home', { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [user, userLoading, navigate]);

  // Cooldown timer for resend button
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setCooldownSeconds(cooldownSeconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  async function handleResendVerification() {
    setError('');
    setSuccess(false);
    setResending(true);

    try {
      // Get password from sessionStorage (stored temporarily during registration)
      const storedPassword = sessionStorage.getItem('coretrak.temp.password');
      
      if (!storedPassword || !email) {
        throw new Error('Unable to resend verification email. Please try signing in again.');
      }

      // Use the helper function that signs in, sends email, then signs out
      await resendVerificationEmailWithCredentials(email, storedPassword);
      
      setSuccess(true);
      setCooldownSeconds(45); // 45 second cooldown (between 30-60s as specified)
      
      // Clear the temporary password after successful resend
      sessionStorage.removeItem('coretrak.temp.password');
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send verification email. Please try again.';
      
      // Check if error contains cooldown information
      if (message.includes('wait')) {
        // Extract cooldown from error message if present
        const match = message.match(/(\d+)\s+second/);
        if (match) {
          setCooldownSeconds(parseInt(match[1], 10));
        }
      }
      
      setError(message);
    } finally {
      setResending(false);
    }
  }

  // Show loading state while checking auth
  if (userLoading) {
    return (
      <Screen title="Loading..." subtitle="Checking authentication...">
        <Card>
          <p className="text-center text-gray-500">Loading...</p>
        </Card>
      </Screen>
    );
  }

  // Don't render if user is already logged in and verified (will redirect)
  if (user && user.emailVerified) {
    return null;
  }

  return (
    <Screen
      title="Check your email"
      subtitle="We've sent you a verification link"
    >
      <Card>
        <div className="space-y-4">
          {/* Success icon and main message */}
          <div className="flex flex-col items-center text-center space-y-3">
            <div className="rounded-full bg-green-100 p-3">
              <FiMail className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                Verification email sent!
              </p>
              <p className="mt-1 text-xs text-gray-600">
                We've sent a verification link to
              </p>
              <p className="mt-0.5 text-xs font-medium text-gray-900">
                {email}
              </p>
            </div>
          </div>

          {/* Important: Check spam/junk */}
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
            <p className="text-xs text-amber-900 font-medium">
              ⚠️ Don't see the email?
            </p>
            <p className="mt-1 text-xs text-amber-800">
              Check your <strong>spam</strong> or <strong>junk</strong> folder. Verification emails sometimes land there.
            </p>
          </div>

          {/* Success message */}
          {success && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 flex items-start gap-2">
              <FiCheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
              <p className="text-xs text-green-900">
                Verification email sent! Please check your inbox.
              </p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3">
              <p className="text-xs text-red-900">{error}</p>
            </div>
          )}

          {/* Resend button */}
          <Button
            fullWidth
            onClick={handleResendVerification}
            disabled={resending || cooldownSeconds > 0}
            variant={cooldownSeconds > 0 ? 'secondary' : 'primary'}
          >
            {resending
              ? 'Sending...'
              : cooldownSeconds > 0
              ? `Resend email (${cooldownSeconds}s)`
              : 'Resend verification email'}
          </Button>

          {/* Back to login link */}
          <div className="text-center text-xs">
            <Link to="/auth/login" className="font-medium text-gray-900">
              Back to login
            </Link>
          </div>
        </div>
      </Card>
    </Screen>
  );
}
